import { File } from '@nextforce/api'
import { useTheme } from '@rneui/themed'
import { showShareHeaderButton } from 'chat/shared/header_share_button'
import { useFileFolderActions } from 'file_folder_repository/context'
import { useChatGroupFileFolders } from 'file_folder_repository/queries/list'
import { useChatGroupMedia } from 'file_folder_repository/queries/media'
import {
  getFileFolderList,
  isImageType,
  isVideoType,
  sortResourceList,
} from 'file_folder_repository/util'
import { ChatFileViewerScreenProps } from 'home/stack/types'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'
import { StatusBoundary } from 'shared/components/status_boundary'
import { showHeaderTitle } from 'shared/header/header_title'
import { convertFileToAttachment, shareAttachment } from 'shared/utils'
import { ChatDocumentViewerView } from './view'

export function ChatFileViewerScreen({
  navigation,
  route,
}: ChatFileViewerScreenProps) {
  const { fileId, conversationId, currentFolderId } = route.params
  const { sortOption } = useFileFolderActions()
  const { theme } = useTheme()

  const fileFolderRepositoryQuery = useChatGroupFileFolders(
    conversationId,
    currentFolderId
  )
  const chatGroupMediaQuery = useChatGroupMedia(conversationId)

  const sortedFileFolderList = useMemo(
    () =>
      sortResourceList(
        [
          ...getFileFolderList(fileFolderRepositoryQuery.data),
          ...(chatGroupMediaQuery.data && currentFolderId === undefined
            ? chatGroupMediaQuery.data
            : []),
        ],
        sortOption
      ),

    [
      chatGroupMediaQuery.data,
      currentFolderId,
      fileFolderRepositoryQuery.data,
      sortOption,
    ]
  )

  const imagesAndVideos = sortedFileFolderList.filter(
    document =>
      document.type === 'file' &&
      (isImageType(document.currentVersion?.fileMimeType) ||
        isVideoType(document.currentVersion?.fileMimeType))
  ) as File[]

  const [currentIndex, setCurrentIndex] = useState<number>()
  const [isShareLoading, setShareLoading] = useState<boolean>(false)

  const onShareAttachment = useCallback(async () => {
    setShareLoading(true)
    if (imagesAndVideos && currentIndex !== undefined) {
      const currentFile = imagesAndVideos[currentIndex]
      if (currentFile) {
        const attachment = convertFileToAttachment(currentFile)
        await shareAttachment(attachment)
      }
    }
    setShareLoading(false)
  }, [currentIndex, imagesAndVideos])

  useLayoutEffect(() => {
    if (imagesAndVideos && currentIndex !== undefined) {
      const currentFileName = imagesAndVideos[currentIndex]?.name
      showHeaderTitle(navigation, currentFileName)
    }
  }, [currentIndex, imagesAndVideos, navigation])

  useEffect(() => {
    if (fileId && currentIndex === undefined && imagesAndVideos) {
      const index = imagesAndVideos.findIndex(file => file.id === fileId)
      if (index !== -1) {
        setCurrentIndex(index)
      }
    }
  }, [chatGroupMediaQuery.data, currentIndex, fileId, imagesAndVideos])

  useLayoutEffect(() => {
    if (imagesAndVideos && currentIndex !== undefined) {
      const currentFile = imagesAndVideos[currentIndex]
      if (currentFile) {
        showShareHeaderButton({
          navigation,
          theme,
          onPress: onShareAttachment,
          loading: isShareLoading,
        })
      }
    }
  }, [
    currentIndex,
    imagesAndVideos,
    isShareLoading,
    navigation,
    onShareAttachment,
    theme,
  ])

  return (
    <StatusBoundary
      error={chatGroupMediaQuery.error}
      isLoading={chatGroupMediaQuery.isLoading}
    >
      <ChatDocumentViewerView
        mediaFiles={imagesAndVideos}
        currentIndex={currentIndex}
        setCurrentIndex={setCurrentIndex}
      />
    </StatusBoundary>
  )
}
